/**
 * 슈퍼관리자 대시보드 — 지점별 「재등록 성공률」.
 *
 * 관리자웹 센터 통계(`Feed/FeedCenterStatisticsRanking.tsx`)의 「재등록 성공률」과
 * **같은 이름·같은 문구·같은 표시 형식**을 쓴다. 이름·분자·분모 말은
 * `RE_REGISTRATION_SUCCESS_NAMES` 한 벌을 그대로 가져온다.
 *
 * 서버 계약(`/v1/superadmins/dashboard/overview` 의 `centers[i]`,
 * `center-detail` 의 `center`):
 *   reRegistrationSuccess?: {
 *     cohortMonth: 'YYYY-MM'; rate: number | null (이미 0~100);
 *     succeededMemberCount; endedMemberCount; notYetMemberCount;
 *     dataQuality: 'EXACT' | 'ESTIMATED' | 'MIXED'; failed: boolean;
 *   }
 *
 * 🔴 값이 없으면(구 서버라 필드 자체가 없음 · 집계 실패 · rate null) **null 로 둔다.**
 *    0 으로 바꾸면 「재등록 0%」 지점으로 읽히고 순위 맨 아래에 실측값처럼 선다.
 */
import { RE_REGISTRATION_SUCCESS_NAMES } from '../Feed/coachPerformanceModel';

export { RE_REGISTRATION_SUCCESS_NAMES };

export type CenterReRegistrationSuccess = {
    cohortMonth: string;
    rate: number | null;
    succeededMemberCount: number;
    endedMemberCount: number;
    notYetMemberCount: number;
    dataQuality: 'EXACT' | 'ESTIMATED' | 'MIXED';
    failed: boolean;
};

/** 지점 행에 펼쳐 넣는 필드 — 센터 통계의 FlatTrainer 필드 이름과 같다. */
export type CenterReRegistrationSuccessFields = {
    reRegistrationSuccessRate: number | null;
    reRegistrationSucceededMemberCount?: number;
    reRegistrationEndedMemberCount?: number;
    /** 'YYYY-MM' — 서버가 집계한 달. 필드가 없는 구 서버면 undefined */
    reRegistrationSuccessCohortMonth?: string;
    /** 이 지점의 이 달 집계가 실패했는가 */
    reRegistrationSuccessFailed: boolean;
    /** 근거 모달 상태 탭 숫자(아직 미재등록) — 서버 값 그대로 */
    reRegistrationNotYetMemberCount?: number;
    reRegistrationSuccessDataQuality?: CenterReRegistrationSuccess['dataQuality'];
};

const finiteOrUndefined = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;

/**
 * 서버의 `reRegistrationSuccess` 한 덩어리를 지점 행 필드로 옮긴다.
 *
 * - 필드 없음(구 서버) → 값 null, 분자·분모 없음
 * - `failed: true` → 값 null, 분자·분모 없음 (서버가 0/0 을 같이 보내도 쓰지 않는다 —
 *   「0명 중 0명」은 근거가 아니다)
 * - 정상 → rate 를 **그대로** 쓴다 (이미 0~100, 100 을 곱하지 않는다)
 */
export const toCenterReRegistrationSuccessFields = (raw: unknown): CenterReRegistrationSuccessFields => {
    if (!raw || typeof raw !== 'object') {
        return { reRegistrationSuccessRate: null, reRegistrationSuccessFailed: false };
    }
    const r = raw as Partial<CenterReRegistrationSuccess>;
    const cohortMonth = typeof r.cohortMonth === 'string' ? r.cohortMonth : undefined;
    if (r.failed === true) {
        return {
            reRegistrationSuccessRate: null,
            reRegistrationSuccessCohortMonth: cohortMonth,
            reRegistrationSuccessFailed: true,
        };
    }
    return {
        reRegistrationSuccessRate: finiteOrUndefined(r.rate) ?? null,
        reRegistrationSucceededMemberCount: finiteOrUndefined(r.succeededMemberCount),
        reRegistrationEndedMemberCount: finiteOrUndefined(r.endedMemberCount),
        reRegistrationSuccessCohortMonth: cohortMonth,
        reRegistrationSuccessFailed: false,
        reRegistrationNotYetMemberCount: finiteOrUndefined(r.notYetMemberCount),
        reRegistrationSuccessDataQuality: isDataQuality(r.dataQuality) ? r.dataQuality : undefined,
    };
};

const isDataQuality = (v: unknown): v is CenterReRegistrationSuccess['dataQuality'] =>
    v === 'EXACT' || v === 'ESTIMATED' || v === 'MIXED';

/**
 * 강사 행의 지점 분해 — 근거 모달의 지점 탭. 서버 `trainers[i].reRegistrationSuccess.centers[]` 순서 그대로.
 * 🔴 분모(정상 종료)가 0 인 지점은 뺀다 — 「0명 중 0명」은 근거가 아니다.
 */
export type ReRegistrationSuccessCenterBreakdown = {
    centerId: string;
    centerName: string;
    succeededMemberCount: number;
    endedMemberCount: number;
    notYetMemberCount?: number;
    rate: number | null;
};

export type TrainerReRegistrationSuccessFields = CenterReRegistrationSuccessFields & {
    reRegistrationSuccessCenters: ReRegistrationSuccessCenterBreakdown[];
};

/**
 * overview `trainers[i].reRegistrationSuccess` → 강사 행 필드.
 * 값·분자·분모·실패 처리는 지점과 **같은 함수**를 쓴다(여러 지점 강사는 서버가 합산해 보낸 값 그대로).
 * failed 거나 필드가 없으면 지점 분해도 비운다.
 */
export const toTrainerReRegistrationSuccessFields = (raw: unknown): TrainerReRegistrationSuccessFields => {
    const base = toCenterReRegistrationSuccessFields(raw);
    if (!raw || typeof raw !== 'object' || base.reRegistrationSuccessFailed) {
        return { ...base, reRegistrationSuccessCenters: [] };
    }
    const list = (raw as { centers?: unknown }).centers;
    const centers: ReRegistrationSuccessCenterBreakdown[] = [];
    if (Array.isArray(list)) {
        list.forEach((c: any) => {
            if (!c || typeof c !== 'object') return;
            const centerId = typeof c.centerId === 'string' ? c.centerId : c.centerId != null ? String(c.centerId) : '';
            const ended = finiteOrUndefined(c.endedMemberCount);
            if (!centerId || ended == null || ended <= 0) return;
            centers.push({
                centerId,
                centerName: typeof c.centerName === 'string' && c.centerName ? c.centerName : '지점',
                succeededMemberCount: finiteOrUndefined(c.succeededMemberCount) ?? 0,
                endedMemberCount: ended,
                notYetMemberCount: finiteOrUndefined(c.notYetMemberCount),
                rate: finiteOrUndefined(c.rate) ?? null,
            });
        });
    }
    return { ...base, reRegistrationSuccessCenters: centers };
};

/**
 * 막대를 눌러 근거를 열 수 있는가 — 관리자웹 `canOpenEvidence` 와 같은 뜻.
 * 값이 없으면(구 서버·집계 실패·분모 0) 열지 않는다. 강사 행은 지점 분해가 하나라도 있어야 한다.
 */
export const canOpenReRegistrationSuccessEvidence = (
    row: {
    reRegistrationSuccessRate?: number | null;
    reRegistrationSuccessFailed?: boolean;
    reRegistrationEndedMemberCount?: number;
    reRegistrationSuccessCohortMonth?: string;
    reRegistrationSuccessCenters?: ReRegistrationSuccessCenterBreakdown[];
    },
    /** 🔴 그 랭킹이 다시 불러오는 중이면 열지 않는다 — 막대는 이전 조회값이라 근거와 달이 어긋날 수 있다 */
    options?: { isFetching?: boolean }
): boolean => {
    if (options?.isFetching) return false;
    if (row.reRegistrationSuccessFailed) return false;
    if (typeof row.reRegistrationSuccessRate !== 'number' || !Number.isFinite(row.reRegistrationSuccessRate)) return false;
    if (!row.reRegistrationEndedMemberCount || row.reRegistrationEndedMemberCount <= 0) return false;
    if (!/^\d{4}-\d{2}$/.test(row.reRegistrationSuccessCohortMonth ?? '')) return false;
    if (row.reRegistrationSuccessCenters !== undefined && row.reRegistrationSuccessCenters.length === 0) return false;
    return true;
};

/** Date → 'YYYY-MM' (로컬 달). 랭킹이 표시 중인 달과 cohortMonth 를 비교할 때 쓴다. */
export const monthKeyOf = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/**
 * 🔴 행의 cohortMonth 가 랭킹이 표시 중인 달과 다르면 재등록 성공률을 **값 없음**으로 돌린다.
 *
 * 강사 조회는 keepPreviousData 이고, 실패·미응답이면 지점 랭킹 조회 결과로 폴백한다(구 달 데이터).
 * 그대로 두면 「8월 기준」 랭킹에 9월 값·9월 근거가 뜬다. 달이 다르면(또는 cohortMonth 가 없으면 — 구 서버)
 * 값 null·분자/분모 없음·실패 표시 없음·지점 분해 없음 → 「-」, 맨 뒤, 등수 없음, 클릭 불가.
 * 달이 같으면 행을 **그대로** 돌려준다(같은 참조).
 */
export const alignReRegistrationSuccessToMonth = <T extends Record<string, any>>(row: T, expectedMonth: string): T => {
    if (!row || (expectedMonth && row.reRegistrationSuccessCohortMonth === expectedMonth)) return row;
    return {
        ...row,
        reRegistrationSuccessRate: null,
        reRegistrationSucceededMemberCount: undefined,
        reRegistrationEndedMemberCount: undefined,
        reRegistrationNotYetMemberCount: undefined,
        reRegistrationSuccessFailed: false,
        ...(Array.isArray(row.reRegistrationSuccessCenters) ? { reRegistrationSuccessCenters: [] } : {}),
    };
};

/**
 * 「N명 중 M명 · 」 — 센터 통계 `ratioOf` 와 같은 문자열.
 * 🔴 분모가 없거나 0 이면 적지 않는다(null).
 */
export const formatReRegistrationSuccessRatio = (row: {
    reRegistrationEndedMemberCount?: number;
    reRegistrationSucceededMemberCount?: number;
}): string | null => {
    const ended = row.reRegistrationEndedMemberCount;
    if (ended == null || ended === 0) return null;
    return `${ended}명 중 ${row.reRegistrationSucceededMemberCount ?? 0}명 · `;
};

/** 막대 툴팁 — 센터 통계와 같은 문장. */
export const formatReRegistrationSuccessTooltip = (
    row: { reRegistrationEndedMemberCount?: number; reRegistrationSucceededMemberCount?: number },
    displayVal: string | number
): string =>
    `${RE_REGISTRATION_SUCCESS_NAMES.numerator} ${row.reRegistrationSucceededMemberCount ?? 0}명 / ${RE_REGISTRATION_SUCCESS_NAMES.denominator} ${row.reRegistrationEndedMemberCount ?? 0}명 = ${displayVal}`;

/** KPI 칸 제목의 달 — 서버 cohortMonth 우선, 없으면 fallback 달(1~12). */
export const reRegistrationSuccessMonthLabel = (cohortMonth: string | undefined, fallbackMonth: number): number => {
    const m = /^\d{4}-(\d{2})$/.exec(cohortMonth ?? '');
    const n = m ? Number(m[1]) : NaN;
    return n >= 1 && n <= 12 ? n : fallbackMonth;
};

/**
 * 측정값 내림차순, 🔴 값이 없는 행(null·undefined·NaN)은 **맨 뒤**(원래 순서 유지).
 * 등수(`rankOf`)도 측정된 행에만 붙인다 — 센터 통계 `buildMeasuredRanking` 과 같다.
 */
export const sortMeasuredFirst = <T extends Record<string, any>>(
    rows: T[],
    key: string
): { sorted: T[]; rankOf: (row: T) => number | null } => {
    const measured: { row: T; index: number; value: number }[] = [];
    const unmeasured: T[] = [];
    rows.forEach((row, index) => {
        const v = row[key];
        if (typeof v === 'number' && Number.isFinite(v)) measured.push({ row, index, value: v });
        else unmeasured.push(row);
    });
    measured.sort((a, b) => b.value - a.value || a.index - b.index);
    const rank = new Map<T, number>();
    measured.forEach((e, i) => rank.set(e.row, i + 1));
    return {
        sorted: [...measured.map((e) => e.row), ...unmeasured],
        rankOf: (row: T) => rank.get(row) ?? null,
    };
};
